import { useState, useEffect, useCallback } from 'react';
import { TimeRange, PricePoint } from '../types';
import { cgGet, cgErrorMsg, CG_MAX_DAYS } from '../utils/coingecko';

// days = explicit day count. `interval` is intentionally omitted so the Pro API
// auto-selects granularity (5-min ≤1d, hourly ≤90d, daily beyond).
const PARAMS: Record<TimeRange, { days: number }> = {
  '24H': { days: 1 },
  '1W':  { days: 7 },
  '1M':  { days: 30 },
  '3M':  { days: 90 },
  '6M':  { days: 180 },
  '1Y':  { days: 365 },
  'MAX': { days: CG_MAX_DAYS },  // pro-api rejects days='max' — request the plan bound
};

const TIMEOUT: Record<TimeRange, number> = {
  '24H': 12_000,
  '1W':  12_000,
  '1M':  12_000,
  '3M':  15_000,
  '6M':  15_000,
  '1Y':  15_000,
  'MAX': 20_000,
};

// Deduplication: same coinId+range shares one in-flight request (handles React Strict Mode double-invoke)
const inFlight = new Map<string, Promise<PricePoint[]>>();

async function fetchPoints(coinId: string, range: TimeRange): Promise<PricePoint[]> {
  const key = `${coinId}_${range}`;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const { days } = PARAMS[range];

  const promise = cgGet(`/coins/${coinId}/market_chart`, {
    params: {
      vs_currency: 'usd',
      days,
    },
    timeout: TIMEOUT[range],
  }).then(({ data }) => {
    const pts: PricePoint[] = ((data as any).prices as [number, number][]).map(
      ([ts, price]) => ({ timestamp: ts, price })
    );
    console.log(
      `[MyCryptoPlanner] ${coinId} ${range}: ${pts.length} points`,
      pts.length > 0
        ? `(${new Date(pts[0].timestamp).toLocaleDateString()} → ${new Date(pts[pts.length - 1].timestamp).toLocaleDateString()})`
        : '(no data)'
    );
    return pts;
  }).finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, promise);
  return promise;
}

export interface CoinHistoryResult {
  points: PricePoint[];
  loading: boolean;
  error: string | null;
  high: number;
  low: number;
  open: number;
  close: number;
  changePercent: number;
  retry: () => void;
}

export function useCoinHistory(coinId: string, range: TimeRange): CoinHistoryResult {
  const [points,     setPoints]     = useState<PricePoint[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const pts = await fetchPoints(coinId, range);
        if (!cancelled) { setPoints(pts); setLoading(false); }
      } catch (err) {
        console.error(`[MyCryptoPlanner] Failed to fetch ${coinId} ${range}:`, err);
        if (!cancelled) {
          setError(cgErrorMsg(err));
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [coinId, range, retryCount]);

  const high = points.length ? points.reduce((m, p) => (p.price > m ? p.price : m), -Infinity) : 0;
  const low  = points.length ? points.reduce((m, p) => (p.price < m ? p.price : m),  Infinity) : 0;
  const open          = points[0]?.price ?? 0;
  const close         = points[points.length - 1]?.price ?? 0;
  const changePercent = open > 0 ? ((close - open) / open) * 100 : 0;

  return { points, loading, error, high, low, open, close, changePercent, retry };
}
